import { Link, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Clock, X as XIcon, Star } from 'lucide-react';
import { getRecentVisits, clearRecentVisits, getPinnedPages } from '@/utils/recentlyVisited';

const STORAGE_KEY = 'dowurk-mobile-nav-open-sections';

function readOpenSections() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeOpenSection(id, isOpen) {
  try {
    const current = readOpenSections();
    if (isOpen) current[id] = true;
    else delete current[id];
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch { /* sessionStorage disabled — silent */ }
}

// SLIM PUBLIC NAV mirrors DesktopNav.js (Feb 2026 IA re-org — compressed to 5).
// About / Start Your Business / Run Your Nonprofit / Community & Support / Give.

// 1. ABOUT — first per user feedback. Stripped of duplicates from other menus.
const aboutLinks = [
  { to: '/about', label: 'About Us' },
  { to: '/our-pledge', label: 'Our Pledge' },
  { to: '/why-dowurk', label: 'Why DowUrk?', style: 'text-[#006847] font-medium hover:bg-emerald-50' },
  { to: '/partnerships', label: 'Partnerships' },
  { to: '/press-release', label: 'Press Release' },
  { to: '/contact', label: '✉️ Contact Us' },
  { to: '/tools', label: '🛠️ All Tools Directory', style: 'text-white bg-gradient-to-r from-emerald-700 to-emerald-900 hover:from-emerald-800 hover:to-emerald-950 font-bold mb-1' },
];

const aboutMembershipLinks = [
  { to: '/premium', label: '👑 Premium Membership', style: 'text-yellow-700 font-medium hover:bg-yellow-50' },
  { to: '/enterprise', label: '🏛️ Enterprise Portal', style: 'text-slate-700 hover:bg-slate-100' },
  { to: '/fundraiser', label: '💚 Dream Project', style: 'text-emerald-700 hover:bg-emerald-50' },
  { to: '/shop', label: '🛒 Shop Merch' },
];

// 2. START YOUR BUSINESS — merges Find Capital + For Entrepreneurs
const startYourBusinessLinks = [
  { to: '/launch-roadmap', label: '🚀 10-Step Launch Roadmap', style: 'text-white bg-gradient-to-r from-emerald-600 to-emerald-800 font-bold mb-1' },
  { to: '/business-formation', label: '📝 Business Formation Wizard' },
  { to: '/business-plan', label: '📝 AI Business Plan Builder', style: 'text-white bg-purple-700 font-semibold mb-1' },
  { to: '/business-structure', label: '⚖️ Business Structure Chooser' },
  { to: '/launch-calculator', label: '🧮 Startup Cost Calculator' },
  { to: '/grant-concierge', label: '🤖 Grant Concierge AI', style: 'text-white bg-[#006847] font-bold mb-1' },
  { to: '/grant-scout', label: '🎯 Grant Scout (Grants.gov)', style: 'text-white bg-emerald-700 font-semibold mb-1' },
  { to: '/compliance-concierge', label: '🛡️ Stay Right (Compliance)', style: 'text-white bg-amber-600 font-semibold mb-1' },
  { to: '/federal-contracting', label: '🏛️ Federal Contracting', style: 'text-white bg-blue-700 font-semibold mb-1' },
  { to: '/capital-marketplace', label: '💰 Capital & Lending Marketplace' },
  { to: '/lender-match', label: '🏦 Louisiana Lender Match' },
  { to: '/capital-readiness', label: '📊 Capital Readiness Hub' },
  { to: '/defi-lending', label: '🪙 DeFi Lending' },
  { to: '/financial-literacy', label: '📊 Financial Literacy' },
  { to: '/businesses', label: '📋 Business Directory', style: 'bg-emerald-100 text-emerald-900 font-semibold mb-1' },
  { to: '/business-map', label: '🗺️ Interactive Business Map', style: 'bg-sky-100 text-sky-900 font-semibold mb-1' },
  { to: '/service-providers', label: '🤝 Service Provider Marketplace' },
  { to: '/professional-services', label: '⭐ Professional Services' },
  { to: '/pitch-practice', label: '🎤 AI Pitch Practice', style: 'text-white bg-rose-600 font-semibold mb-1' },
  { to: '/sop-builder', label: '📋 SOP Builder' },
  { to: '/workforce', label: '💼 wUrkForce Development', style: 'text-white bg-gradient-to-r from-blue-700 to-emerald-700 font-bold mb-1' },
  { to: '/resources', label: '📖 Resource Library' },
  { to: '/blog', label: '✍️ Blog & Podcast' },
  { to: '/global-biz-resources', label: '🌍 Global Biz Resources' },
  { to: '/louisiana-biz-resources', label: '⚜️ Louisiana Biz Resources' },
];

// 3. RUN YOUR NONPROFIT — merges For Nonprofits + Prove Impact
const runYourNonprofitLinks = [
  { to: '/nonprofits', label: '🏛️ PROOF for Cohorts', style: 'bg-gradient-to-r from-emerald-700 to-emerald-900 text-white font-bold mb-1' },
  { to: '/pricing', label: '💎 PROOF Pricing & Plans', style: 'bg-amber-500 text-white font-bold mb-1' },
  { to: '/nonprofits/signup', label: '✨ Apply for PROOF Free', style: 'bg-emerald-600 text-white font-bold mb-1' },
  { to: '/nonprofits/start-a-nonprofit', label: '📘 How to Start a Nonprofit (LA)' },
  { to: '/nonprofits/advocacy', label: '📣 Federal Funding Advocacy' },
  { to: '/nonprofit-grants', label: '🏢 Nonprofit Grants Pipeline', style: 'text-white bg-teal-700 font-semibold mb-1' },
  { to: '/grants/tracker', label: '📅 Grant Deadline Tracker' },
  { to: '/compliance-concierge', label: '🛡️ Stay Right (Compliance)' },
  { to: '/nonprofit-ops', label: '🏢 Nonprofit Ops Suite' },
  { to: '/dowurk/impact', label: '📊 PROOF Network Impact', style: 'bg-indigo-700 text-white font-bold mb-1' },
  { to: '/proof', label: '🎯 PROOF Overview', style: 'bg-emerald-700 text-white font-bold mb-1' },
  { to: '/badges', label: '🏅 Open Badges' },
  { to: '/proof/pulse', label: '📈 PROOF Scoreboard' },
  { to: '/simulators', label: '🕶️ Immersive Simulators' },
  { to: '/nonprofits/directory', label: '📇 PROOF Directory' },
  { to: '/nonprofits/proof-free-tier', label: '🎁 Free Tier Details' },
  { to: '/nonprofits/embed-kit', label: '📎 PROOF Embed Kit' },
  { to: '/needs-assessment', label: '🤝 Community Needs Assessment' },
  { to: '/proof/resilience-pathway', label: '🛡️ 30-Day Resilience Pathway', style: 'text-emerald-700 font-semibold' },
  { to: '/proof/resilience-wall', label: '🧱 Resilience Wall' },
];

// 4. COMMUNITY & SUPPORT (with Seven F's nested)

// 4a. SEVEN F's — nested expandable inside Community
const sevenFsLinks = [
  { to: '/framework', label: "🎯 The Seven F's FramewUrk", style: "text-[#006847] font-bold hover:bg-emerald-50" },
  { to: '/faith', label: '🧭 Faith wUrks', style: 'text-amber-700 font-semibold hover:bg-amber-50' },
  { to: '/fitness', label: '💪 wUrkFit', style: 'text-rose-700 font-semibold hover:bg-rose-50' },
  { to: '/foundation', label: '📚 School 4 Thought', style: 'text-purple-700 font-semibold hover:bg-purple-50' },
  { to: '/food', label: '🍽️ Farm2Table', style: 'text-orange-700 font-semibold hover:bg-orange-50' },
  { to: '/legacystudio', label: '🎬 LegacyStudio', style: 'text-violet-700 font-semibold hover:bg-violet-50' },
  { to: '/financial-literacy', label: '💰 Financial Literacy', style: 'text-emerald-700 font-semibold hover:bg-emerald-50' },
];

// 4b. COMMUNITY & SUPPORT (top-level)
const communityLinks = [
  { to: '/youth-track', label: '🎓 Youth Track (13–18)', style: 'bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold mb-1' },
  { to: '/community-track', label: '💼 Community Track', style: 'bg-gradient-to-r from-emerald-600 to-teal-700 text-white font-bold mb-1' },
  { to: '/mental-health', label: '🧠 Mental Health Center', style: 'bg-gradient-to-r from-emerald-600 to-amber-500 text-white font-semibold mb-1' },
  { to: '/disaster-hub', label: '🌪️ Disaster Hub', style: 'bg-amber-500 text-white font-semibold mb-1' },
  { to: '/veterans', label: '🎖️ Veterans Support', style: 'bg-[#006847] text-white font-semibold mb-1' },
  { to: '/forum', label: '💬 Community Discussion', style: 'text-[#388E3C] font-semibold' },
  { to: '/community', label: '🌐 Community Wall' },
  { to: '/wins', label: '🏆 The Win Wall' },
  { to: '/mentors', label: '🤝 Mentor Match' },
  { to: '/events', label: '📅 Events' },
  { to: '/volunteer-hub', label: '💝 Volunteer Hub' },
  { to: '/skills-barter', label: '🤝 Skills Barter' },
  { to: '/gratitude-wall', label: '🙏 Gratitude Wall' },
];

const communitySubSections = [
  { title: "Seven F's Programs", links: sevenFsLinks },
];

function LinkList({ links, onClose }) {
  return (
    <>
      {links.map((link) => (
        link.external && link.href ? (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className={`block px-4 py-2 rounded text-sm ${link.style || 'text-gray-700 hover:bg-gray-100'}`}
          >
            {link.label} ↗
          </a>
        ) : (
          <Link
            key={link.to}
            to={link.to}
            onClick={onClose}
            className={`block px-4 py-2 rounded text-sm ${link.style || 'text-gray-700 hover:bg-gray-100'}`}
          >
            {link.label}
          </Link>
        )
      ))}
    </>
  );
}

function NestedSection({ title, links, onClose, sectionId }) {
  const ref = useRef(null);
  const persistKey = `sub:${sectionId}`;
  const initiallyOpen = readOpenSections()[persistKey] === true;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = () => writeOpenSection(persistKey, el.open);
    el.addEventListener('toggle', handler);
    return () => el.removeEventListener('toggle', handler);
  }, [persistKey]);

  return (
    <details ref={ref} open={initiallyOpen} className="group/sub ml-2 border-l-2 border-[#A4D65E]/40">
      <summary
        className="px-3 py-2 text-xs font-semibold text-[#388E3C] uppercase cursor-pointer list-none flex items-center justify-between hover:bg-[#A4D65E]/10 rounded"
        data-testid={`mobile-subsection-${title.replace(/[^a-z0-9]+/gi, '-').toLowerCase().replace(/^-|-$/g, '')}`}
      >
        <span className="flex items-center gap-1">
          <span className="text-[#006847]">›</span> {title}
        </span>
        <span className="group-open/sub:rotate-180 transition-transform text-[10px]">▼</span>
      </summary>
      <div className="pl-2 pt-1">
        <LinkList links={links} onClose={onClose} />
      </div>
    </details>
  );
}

function MobileMenuSection({ title, links, subSections = null, ctaLinks = null, onClose, defaultOpen = false, testId, sectionId }) {
  const ref = useRef(null);
  const persistKey = `top:${sectionId || title}`;
  const stored = readOpenSections()[persistKey];
  const initiallyOpen = stored === true || (stored === undefined && defaultOpen);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = () => writeOpenSection(persistKey, el.open);
    el.addEventListener('toggle', handler);
    return () => el.removeEventListener('toggle', handler);
  }, [persistKey]);

  return (
    <details ref={ref} open={initiallyOpen} className="group">
      <summary
        className="px-4 py-2 text-xs font-bold text-[#006847] uppercase cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 rounded"
        data-testid={testId}
      >
        <span>{title}</span>
        <span className="group-open:rotate-180 transition-transform">▼</span>
      </summary>
      <div className="pl-2">
        {links && <LinkList links={links} onClose={onClose} />}
        {subSections && subSections.length > 0 && (
          <div className="mt-2 space-y-1">
            <p className="px-3 py-1 text-[10px] font-bold text-gray-400 uppercase tracking-wide">Explore</p>
            {subSections.map((sub) => (
              <NestedSection
                key={sub.title}
                title={sub.title}
                links={sub.links}
                onClose={onClose}
                sectionId={`${persistKey}/${sub.title}`}
              />
            ))}
          </div>
        )}
        {ctaLinks && ctaLinks.length > 0 && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <LinkList links={ctaLinks} onClose={onClose} />
          </div>
        )}
      </div>
    </details>
  );
}

export function MobileNav({ isOpen, onClose, onLogout }) {
  const { user } = useAuth();
  const location = useLocation();
  const [recents, setRecents] = useState([]);
  const [pinned, setPinned] = useState([]);

  // Recompute recents + pinned each time the drawer opens or the current path changes
  useEffect(() => {
    if (isOpen) {
      setRecents(getRecentVisits(location.pathname));
      setPinned(getPinnedPages());
    }
    const pinListener = () => setPinned(getPinnedPages());
    window.addEventListener('dowurk:pins-changed', pinListener);
    return () => window.removeEventListener('dowurk:pins-changed', pinListener);
  }, [isOpen, location.pathname]);

  const handleClearRecents = (e) => {
    e.stopPropagation();
    e.preventDefault();
    clearRecentVisits();
    setRecents([]);
  };

  if (!isOpen) return null;

  return (
    <div data-testid="compact-navigation" className="min-[1800px]:hidden py-4 space-y-2 border-t border-gray-200 max-h-[70vh] overflow-y-auto">
      {/* Pinned Pages */}
      {pinned.length > 0 && (
        <div className="bg-gradient-to-r from-[#FFC107]/15 to-[#FF9800]/10 rounded-lg mx-2 mb-2 border border-[#FFC107]/40" data-testid="mobile-pinned-section">
          <div className="flex items-center gap-2 px-3 py-2 text-xs font-bold text-[#B8860B] uppercase">
            <Star className="h-3.5 w-3.5 fill-[#FFC107] text-[#FFC107]" />
            Pinned
          </div>
          <div className="px-2 pb-2 space-y-0.5">
            {pinned.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={onClose}
                className={`block px-3 py-1.5 rounded text-sm ${item.style || 'text-gray-700 hover:bg-white'}`}
                data-testid={`mobile-pinned-${item.to}`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Recently Visited */}
      {recents.length > 0 && (
        <div className="bg-gradient-to-r from-[#A4D65E]/10 to-[#006847]/10 rounded-lg mx-2 mb-2 border border-[#A4D65E]/30" data-testid="mobile-recents-section">
          <div className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-2 text-xs font-bold text-[#006847] uppercase">
              <Clock className="h-3.5 w-3.5" />
              Recently Visited
            </div>
            <button
              onClick={handleClearRecents}
              className="p-1 hover:bg-white/60 rounded text-gray-500 hover:text-gray-700"
              aria-label="Clear recently visited"
              data-testid="mobile-recents-clear-btn"
            >
              <XIcon className="h-3 w-3" />
            </button>
          </div>
          <div className="px-2 pb-2 space-y-0.5">
            {recents.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={onClose}
                className={`block px-3 py-1.5 rounded text-sm ${item.style || 'text-gray-700 hover:bg-white'}`}
                data-testid={`mobile-recent-${item.to}`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* SLIM PUBLIC NAV — mirrors DesktopNav order. About first. */}

      {/* 1. ABOUT */}
      <MobileMenuSection
        title="About"
        links={aboutLinks}
        ctaLinks={aboutMembershipLinks}
        onClose={onClose}
        defaultOpen
        testId="mobile-section-about"
        sectionId="about"
      />

      {/* 2. START YOUR BUSINESS (merges Find Capital + For Entrepreneurs) */}
      <MobileMenuSection
        title="🚀 Start Your Business"
        links={startYourBusinessLinks}
        onClose={onClose}
        testId="mobile-section-start-business"
        sectionId="startBusiness"
      />

      {/* 3. RUN YOUR NONPROFIT (merges For Nonprofits + Prove Impact) */}
      <MobileMenuSection
        title="🏛️ Run Your Nonprofit"
        links={runYourNonprofitLinks}
        onClose={onClose}
        testId="mobile-section-run-nonprofit"
        sectionId="runNonprofit"
      />

      {/* 4. COMMUNITY & SUPPORT (with Seven F's nested) */}
      <MobileMenuSection
        title="👥 Community & Support"
        links={communityLinks}
        subSections={communitySubSections}
        onClose={onClose}
        testId="mobile-section-community"
        sectionId="communityNew"
      />

      {/* 5. GIVE — standalone prominent link */}
      <Link
        to="/donate"
        onClick={onClose}
        className="block mx-2 my-1 px-4 py-3 rounded-lg bg-gradient-to-r from-rose-500 to-rose-600 text-white font-bold text-center hover:from-rose-600 hover:to-rose-700 shadow"
        data-testid="mobile-nav-give"
      >
        ❤️ Give
      </Link>

      {/* My Dashboard quick-access for logged-in users (auth-required tools live there) */}
      {user && (
        <Link
          to="/dashboard"
          onClick={onClose}
          className="block mx-2 my-1 px-4 py-3 rounded-lg bg-gradient-to-r from-slate-900 to-slate-700 text-white font-bold text-center hover:from-slate-800 hover:to-slate-600 shadow"
          data-testid="mobile-nav-my-dashboard"
        >
          🧰 My Dashboard & Tools →
        </Link>
      )}

      {/* Account */}
      <div className="border-t pt-2 mt-2">
        {user ? (
          <>
            {(user.user_type === 'admin' || user.email === 'info@dowurktoday.org' || user.email === 'info@dowurktoday.com') && (
              <Link to="/admin" onClick={onClose} className="block px-4 py-2 text-purple-700 hover:bg-purple-50 rounded text-sm font-semibold">Admin Panel</Link>
            )}
            <Link to="/account/settings" onClick={onClose} className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded text-sm">Account Settings</Link>
            <button onClick={() => { onLogout(); onClose(); }} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 rounded text-sm">Logout</button>
          </>
        ) : (
          <>
            <Link to="/login" onClick={onClose} className="block px-4 py-2 text-gray-700 hover:bg-gray-100 rounded text-sm">Login</Link>
            <Link to="/register" onClick={onClose} className="block px-4 py-2 text-[#006847] hover:bg-gray-100 rounded text-sm font-semibold">Sign Up</Link>
            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <button
              type="button"
              onClick={() => {
                const redirectUrl = window.location.origin + '/auth/callback';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full text-left flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded text-sm"
              data-testid="mobile-nav-google-signin"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.44c-.28 1.48-1.12 2.73-2.38 3.57v2.96h3.85c2.25-2.08 3.58-5.14 3.58-8.77z"/>
                <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.85-2.96c-1.07.72-2.44 1.15-4.08 1.15-3.14 0-5.8-2.12-6.75-4.97H1.3v3.06C3.27 21.3 7.31 24 12 24z"/>
                <path fill="#FBBC05" d="M5.25 14.31c-.24-.72-.38-1.49-.38-2.31s.14-1.59.38-2.31V6.63H1.3C.47 8.28 0 10.09 0 12s.47 3.72 1.3 5.37l3.95-3.06z"/>
                <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.27 2.7 1.3 6.63l3.95 3.06C6.2 6.87 8.86 4.75 12 4.75z"/>
              </svg>
              Sign in with Google
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default MobileNav;
